import cx from 'classnames';
import React, { forwardRef, useImperativeHandle } from 'react';
import Overlay from 'react-overlays/Overlay';
import styles from './Popover.module.scss';
import usePresenter from './Popover.presenter';
import type { PopoverHandle, PopoverProps } from './Popover.types';
import { getWidth } from './Popover.utils';

/**
 * Popover component that displays a floating content element in relation to a target element.
 * It can be triggered via different user actions like 'Click' or 'Hover'.
 */
const Popover = forwardRef<PopoverHandle, PopoverProps>((props, ref) => {
  const {
    handleOnHide,
    openPopover,
    containerRef,
    onClickListener,
    onDoubleClickListener,
    targetOnClickListener,
    targetOnDoubleClickListener,
    targetRef,
    onMouseOutListener,
    onMouseOverListener,
    popoverAccessibilityProps,
    targetAccessibilityProps,
    show,
    offset,
    placement,
    contentPosition,
    initialTransformRef,
    hideOnClickAway,
    otherProps
  } = usePresenter(props);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    close: handleOnHide, // Method to close the popover programmatically
    open: openPopover,   // (Optional) Method to open the popover programmatically
  }));

  return (
    <div
      className={cx(styles.popover, props.className)}
      ref={containerRef}
      onClick={onClickListener}
      onDoubleClick={onDoubleClickListener}
      onMouseOut={onMouseOutListener}
      onMouseOver={onMouseOverListener}
      {...popoverAccessibilityProps}
      {...{
        ...otherProps,
        disabled: props.disabled,
      }}
    >
      {/* Target element that triggers the popover */}
      <div
        className={cx(styles.target,
          {
            [styles.disabled]: props.disabled,
          },
        )}
        onClick={targetOnClickListener}
        onDoubleClick={targetOnDoubleClickListener}
        {...targetAccessibilityProps}
        ref={targetRef}
      >
        {React.isValidElement(props.target)
          ? React.cloneElement(props.target, {
            ...(show ? { active: 'true' } : {}),
          })
          : props.target}
      </div>
      <Overlay
        offset={offset}
        onHide={handleOnHide}
        placement={placement}
        rootClose={hideOnClickAway}
        show={show}
        target={targetRef}
        container={props.container || containerRef}
        flip={props.flip}
      >
        {({ props: { style: { transform, ...restStyle }, ...restProps } }) => {
          // Store the initial transform value if it's not set and contentPosition is fixed
          if (contentPosition === 'fixed' && !initialTransformRef.current && transform) {
            initialTransformRef.current = transform;
          }

          return (
            <div
              className={cx(styles.content, props.overlayClassName)}
              {...restProps}
              style={{
                ...restStyle,
                minWidth: getWidth(props.contentProps?.minWidth, targetRef, !props.contentProps?.minWidth),
                width: getWidth(props.contentProps?.width, targetRef),
                position: contentPosition,
                transform: contentPosition === 'fixed'
                  ? initialTransformRef.current
                  : transform
              }}
              onMouseOut={onMouseOutListener}
              onMouseOver={onMouseOverListener}
              tabIndex={show ? 0 : -1}
            >
              {props.content}
            </div>
          );
        }}
      </Overlay>
    </div>
  );
});

export default Popover;
